import { DropdownMenu } from 'radix-ui';
import { Fragment, type ReactElement, type ReactNode } from 'react';

import { cx } from '../../lib/cx';
import styles from './menu.module.css';

export interface MenuItem {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
  /** Отделить чертой сверху: так отбивают действие другого рода, например выход. */
  readonly separated?: boolean;
  readonly disabled?: boolean;
}

export interface MenuProps {
  /** Содержимое кнопки, открывающей меню. */
  trigger: ReactNode;
  items: readonly MenuItem[];
  /** Подпись кнопки для скринридера, если её содержимое не текст. */
  label?: string;
  triggerClassName?: string;
  /**
   * `outlined` — кнопка с рамкой, как бургер навигации: меню действий страницы стоит
   * в полосе рядом с ним и обязано читаться той же кнопкой. `plain` — тихая кнопка
   * аккаунта.
   */
  triggerVariant?: 'plain' | 'outlined';
  disabled?: boolean;
  align?: 'start' | 'end';
}

/**
 * Выпадающее меню действий.
 *
 * Поверх Radix, а не своё: клавиатура (стрелки, Home/End, Escape), возврат фокуса
 * на кнопку при закрытии, закрытие по клику вне и правильные роли — это тот объём,
 * который своя реализация почти наверняка сделает наполовину. Тот же довод, по
 * которому модальное окно и поповер уже стоят на Radix.
 *
 * Живёт в ui-kit, потому что `radix-ui` — зависимость дизайн-системы, а не
 * приложения: приложение не должно тянуть Radix напрямую.
 */
export function Menu({
  trigger,
  items,
  label,
  triggerClassName,
  triggerVariant = 'plain',
  disabled,
  align = 'end',
}: MenuProps): ReactElement {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cx(
          styles.trigger,
          triggerVariant === 'outlined' && styles.triggerOutlined,
          triggerClassName,
        )}
        aria-label={label}
        disabled={disabled}
      >
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={styles.content}
          align={align}
          sideOffset={4}
          collisionPadding={8}
        >
          {items.map((item) => (
            // Фрагмент, а не `<div>`: внутри `role="menu"` допустимы только пункты,
            // группы и разделители, и лишняя обёртка ломала бы это дерево.
            <Fragment key={item.key}>
              {item.separated ? <DropdownMenu.Separator className={styles.separator} /> : null}
              <DropdownMenu.Item
                className={styles.item}
                disabled={item.disabled}
                onSelect={item.onSelect}
              >
                {item.label}
              </DropdownMenu.Item>
            </Fragment>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
